import React, { useEffect, useRef } from 'react';
import './WhyChooseUs.css';

export default function WhyChooseUs() {
  const sectionRef = useRef(null);
  const itemRefs = useRef([]);
  const headingRef = useRef(null);
  const [headingVisible, setHeadingVisible] = React.useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    itemRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.12 }
    );

    const els = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    if (els?.length) {
      els.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeadingVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (headingRef.current) observer.observe(headingRef.current);
    return () => observer.disconnect();
  }, []);

  const splitWords = (text, startDelay = 0) => {
    const words = text.trim().split(/\s+/);
    return words.map((word, i) => (
      <span
        key={`${word}-${i}`}
        className="word-animate"
        style={{ animationDelay: `${startDelay + i * 40}ms` }}
      >
        {word}
        {i < words.length - 1 ? ' ' : ''}
      </span>
    ));
  };

  const steps = [
    {
      num: '01',
      title: 'Certified Refurbished',
      desc: 'All devices are professionally tested, cleaned, and certified to work like new.'
    },
    {
      num: '02',
      numInverted: '02',
      title: '6-Month Warranty',
      desc: 'Every purchase is protected with comprehensive 6-month warranty coverage for peace of mind.'
    },
    {
      num: '03',
      title: '7-Day Easy Returns',
      desc: 'Not satisfied? Return your purchase within 7 days with our hassle-free return policy.'
    }
  ];

  return (
    <section
      id="why-choose-us"
      ref={sectionRef}
      className="relative overflow-hidden py-16 why-choose-us-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* Left: Image */}
          <div
            ref={(el) => { itemRefs.current[0] = el; }}
            className="reveal-left relative"
          >
            <div className="relative overflow-hidden" style={{ aspectRatio: '4/5', borderRadius: '12px' }}>
              <img
                src="/images/home/whychooseus.webp"
                alt="Quality Refurbished Tech Inspection"
                className="w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to bottom, transparent 60%, rgba(0, 99, 184, 0.2) 100%)' }}
              />
            </div>

            {/* Floating stat card */}
            <div
              className="absolute -bottom-6 -right-6 py-6 px-8 hidden md:block floating-stat"
            >
              <div className="font-handwritten text-4xl mb-1 wcu-step-num" style={{ fontFamily: 'Caveat, cursive' }}>99%</div>
              <div className="font-body text-xs font-bold tracking-widest uppercase text-white/60" style={{ fontSize: '10px' }}>Customer Satisfaction</div>
              <div className="font-body text-xs mt-2 text-white/40" style={{ lineHeight: 1.6, fontSize: '10px' }}>
                Join 10000+ happy tech owners.<br />Inspected, Certified, and Trusted.
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div
            ref={(el) => { itemRefs.current[1] = el; }}
            className="reveal-right"
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="wcu-chapter-label">The Nexawave Standard</span>
              <div style={{ width: '40px', height: '1px', background: '#0063B8', opacity: 0.2 }} />
            </div>

            <h2
              ref={headingRef}
              className={`section-fade ${headingVisible ? 'visible' : ''} font-serif-display text-4xl md:text-6xl mb-4 text-[#1A1A1A]`}
              style={{ lineHeight: 1.08 }}
            >
              {splitWords('Premium Tech', 100)}
              <br />
              <em className="wcu-step-num text-teal" style={{ fontStyle: 'normal' }}>
                {splitWords('With a Second Life.', 220)}
              </em>
            </h2>

            <p className="font-body text-base mb-10 text-gray-600 animate-on-scroll anim-in delay-100" style={{ lineHeight: 1.8, maxWidth: '480px' }}>
              Ditch the retail price without sacrificing performance. Whether it's a professional-grade laptop or a gaming monitor, we ensure every piece is restored to perfection.
            </p>

            {/* Steps - Clean design with divider line */}
            <div className="space-y-8 mb-10 animate-on-scroll anim-in delay-200">
              {steps.map((step, i) => (
                <div
                  key={step.num}
                  className="flex gap-6 items-center wcu-step-item"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div
                    className="font-serif-display text-2xl flex-shrink-0 w-10 text-right wcu-step-num"
                    style={{ lineHeight: 1 }}
                  >
                    {step.num}
                  </div>

                  {/* Vertical Strip Line between number and content */}
                  <div style={{ width: '1px', height: '40px', background: '#0063B8', opacity: 0.15 }} />

                  <div>
                    <div className="font-body text-sm font-bold mb-1 text-[#1A1A1A]" style={{ letterSpacing: '0.02em', fontSize: '14px' }}>{step.title}</div>
                    <div className="font-body text-sm text-gray-500" style={{ lineHeight: 1.6, fontSize: '14px', maxWidth: '380px' }}>{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <a
              href="#products"
              className="consign-cta animate-on-scroll anim-in delay-300"
            >
              <span>Explore Our Tech</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
